import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { UserService, HelpKey, RoleItem } from '../../services/user.service';
import { User } from '../../models/user';
import { MountPoint } from '../../models/mount-point';
import { MatDialog } from '@angular/material/dialog';
import { HelpDialogComponent } from '../../pages/help-dialog/help-dialog.component';
import { ConfirmDialogComponent } from '../confirm-dialog/confirm-dialog.component';
import { AcknowledgeDialogComponent } from '../../pages/acknowledge-dialog/acknowledge-dialog.component';

@Component({
  selector: 'app-user-list',
  templateUrl: './user-list.component.html',
  styleUrl: './user-list.component.scss'
})
export class UserListComponent implements OnInit {

  users: User[] = [];
  totalUsers: number = 0;
  selectedUser?: User;
  selectedUserIdForList?: string;
  selectedMountPoint?: MountPoint;
  searchKeywords: string = '';
  loading: boolean = true;
  // pagination
  currentPage: number = 1;
  pageSize: number = 20;
  Math = Math;
  // editing state for User Details
  canEditUser: boolean = false;

  // roles state
  userRoles: RoleItem[] = [];
  enterpriseMode = false;
  pageTitle = 'Users Admin';
  private readonly fullRoleOptions: string[] = ['analyzer', 'downloader', 'ntrip-basic', 'analyzer-pro', 'downloader-pro', 'ntrip-ent', 'ntrip-pro', 'demo', 'admin'];
  private readonly entRoleAllowlist: string[] = ['analyzer', 'downloader', 'ntrip-basic', 'ntrip-ent', 'ntrip-pro'];
  addRoleOptions: string[] = [...this.fullRoleOptions];
  selectedNewRole: string = 'ntrip-pro';

  constructor(
    public userService: UserService,
    public dialog: MatDialog,
    private route: ActivatedRoute
  ) { }

  ngOnInit() {
    this.enterpriseMode = this.route.snapshot.data?.['mode'] === 'enterprise';
    this.pageTitle = this.enterpriseMode ? 'Enterprise Users Admin' : 'Users Admin';
    this.addRoleOptions = this.enterpriseMode ? [...this.entRoleAllowlist] : [...this.fullRoleOptions];
    this.fetchUsersPaged(true);
  }

  canRemoveRole(role: RoleItem): boolean {
    if (!this.enterpriseMode) return true;
    return this.entRoleAllowlist.some(r => r.toLowerCase() === (role?.name || '').toLowerCase());
  }

  openDeleteConfirmDialog(user: User): void {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '360px',
      data: { title: 'Delete User Confirmation', message: 'Are you sure you want to delete this user?' }
    });
  
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.deleteUser(user);
      }
    });
  }
  
  deleteUser(user: User): void {
    this.userService.deleteUser(user).subscribe({
      next: (response) => {
        console.info(response.message);
        this.getFilteredUsers(this.searchKeywords, true);
        this.userService.ntripApplyWithLogging();
      },
      error: (error) => {
        console.error(error);
        const msg = typeof error?.error === 'string'
          ? error.error
          : (error?.error?.message || error?.error?.error || 'Delete failed');
        this.dialog.open(AcknowledgeDialogComponent, { data: msg });
      },
      complete: () => console.log('Delete user request complete')
    });
  }

  loadUser() {
    this.userService.getUser().subscribe((data: User[]) => {    
      this.selectedUser = data[0];
    });
  }

  private reloadSelectedUserById(): void {
    if (!this.selectedUser) return;
    const id = this.selectedUser.userId;
    this.userService.getUserById(id).subscribe({
      next: full => { this.selectedUser = full; },
      error: _ => {}
    });
  }

  onSubmit(): void {
    if (this.selectedUser) {
      this.userService.updateUser(this.selectedUser).subscribe({
        next: (response) => {
          console.info('Update successful'); 
          this.reloadSelectedUserById(); 
          this.dialog.open(AcknowledgeDialogComponent, {
            data: 'User and its mountpoint(s) updated successfully.'
          });
          this.userService.ntripApplyWithLogging();
        },
        error: (error) => {
          console.warn('Update failed:', error);
          this.reloadSelectedUserById();  
          this.dialog.open(AcknowledgeDialogComponent, {
            data: `${error.error}`
          });
        }
      });
    } else {
      console.error('no user selected');
    }
  }

  editUser(user: User): void {
    this.selectedUser = user;
    this.loading = true;
  }

  generateNewMountPointForUser(): void {
    if (this.selectedUser) {
      this.userService.generateNewMountPoint().subscribe({
        next: (response) => {
          console.info('Mountpoint created');
          alert('New mountpoint created successfully.');
        },
        error: (error) => {
          console.error('Failed to create mountpoint:', error);
          alert(`Failed to create mountpoint: ${error.error}`);
        }
      });
    } else {
      console.error('No selected user to create a new mountpoint');
    }
  }
  
  showHelp(key: HelpKey): void {
    const dialogRef = this.dialog.open(HelpDialogComponent, {
      width: '250px',
      data: this.userService.helpData[key]
    });

    dialogRef.afterClosed().subscribe(() => {
      console.log('Help dialog closed');
    });
  }

  getFilteredUsers(keywords: string = this.searchKeywords, keepPage: boolean = false) { this.fetchUsersPaged(!keepPage); }

  fetchUsersPaged(resetPage: boolean = false): void {
    if (resetPage) this.currentPage = 1;
    const pageIdx = Math.max(0, this.currentPage - 1);
    this.userService.getUsersPaged(pageIdx, this.pageSize, this.searchKeywords).subscribe(p => {
      const content = p?.content || [];
      this.users = content.map((x: any) => ({ userId: x.userId, userName: x.userName, email: x.email } as User));
      this.totalUsers = p?.totalElements || this.users.length;
      this.loading = false;
      this.selectedUser = undefined;
      this.selectedUserIdForList = undefined;
    });
  }

  // pagination helpers
  get totalPages(): number { return this.totalUsers === 0 ? 0 : Math.ceil(this.totalUsers / this.pageSize); }

  get pagedUsers(): User[] { return this.users; }

  goToPage(p: number): void {
    this.currentPage = Math.min(Math.max(1, p), this.totalPages);
    this.fetchUsersPaged();
  }
  goFirst(): void { this.goToPage(1); }
  goLast(): void { this.goToPage(this.totalPages); }
  goPrev(): void { if (this.currentPage > 1) { this.currentPage--; this.fetchUsersPaged(); } }
  goNext(): void { if (this.currentPage < this.totalPages) { this.currentPage++; this.fetchUsersPaged(); } }

  get pageWindow(): number[] {
    const total = this.totalPages;
    if (total <= 0) return [];
    const targetCount = Math.min(5, total);
    const windowRadius = Math.floor(targetCount / 2);
    let start = Math.max(1, this.currentPage - windowRadius);
    let end = Math.min(total, start + targetCount - 1);
    start = Math.max(1, Math.min(start, end - targetCount + 1));
    const pages: number[] = [];
    for (let p = start; p <= end; p++) pages.push(p);
    return pages;
  }

  // selection logic: only one selection at a time
  isSelectedInList(u: User): boolean { return this.selectedUserIdForList === u.userId; }
  selectOne(u: User): void {
    // 如果点击的是当前已选用户，则保持选中，不进行清空隐藏
    if (this.selectedUserIdForList === u.userId && this.selectedUser) {
      return;
    }

    this.selectedUserIdForList = u.userId;
    this.selectedUser = this.users.find(x => x.userId === this.selectedUserIdForList);
    if (!this.selectedUser) { this.userRoles = []; this.loading = false; return; }

    this.loading = true;
    const id = this.selectedUser.userId;
    this.loadUserRoles(id);
    this.userService.getUserById(id).subscribe({
      next: full => {
        // 保持 selectedUser 引用不变，仅更新字段，避免传给子组件的对象引用变化导致表单双向绑定失效
        if (this.selectedUser) {
          Object.assign(this.selectedUser, full);
        } else {
          this.selectedUser = full;
        }
        this.loading = false;
      },
      error: _ => {
        this.loading = false;
      }
    });
  }

  loadUserRoles(userId: string): void {
    this.userService.getUserRoles(userId).subscribe({
      next: roles => {
        this.userRoles = Array.isArray(roles) ? roles : [];
        // 过滤下拉：去掉已拥有角色
        const owned = new Set(this.userRoles.map(r => r.name.toLowerCase()));
        const base = this.enterpriseMode ? this.entRoleAllowlist : this.fullRoleOptions;
        this.addRoleOptions = base.filter(r => !owned.has(r.toLowerCase()));
        if (!this.addRoleOptions.includes(this.selectedNewRole)) {
          this.selectedNewRole = this.addRoleOptions.includes('ntrip-pro') ? 'ntrip-pro' : (this.addRoleOptions[0] || 'ntrip-pro');
        }
      },
      error: _ => { this.userRoles = []; }
    });
  }

  onClickRemoveRole(role: RoleItem): void {
    if (!this.selectedUser) return;
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '360px',
      data: { title: 'Remove Role Confirmation', message: `Remove role "${role.name}" from user "${this.selectedUser.userName || this.selectedUser.email || this.selectedUser.userId}"?` }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (!result) return;
      this.userService.removeUserRole(this.selectedUser!.userId, role.id).subscribe({
        next: _ => {
          this.loadUserRoles(this.selectedUser!.userId);
        },
        error: (err) => {
          const msg = typeof err?.error === 'string' ? err.error : (err?.error?.message || 'remove failed');
          this.dialog.open(AcknowledgeDialogComponent, { data: msg });
        }
      });
    });
  }

  onClickAddRole(): void {
    if (!this.selectedUser) return;
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '360px',
      data: { title: 'Add Role Confirmation', message: `Are you sure you want to add this role to the user?\nRole: "${this.selectedNewRole}"\nUser: "${this.selectedUser.userName || this.selectedUser.email || this.selectedUser.userId}"` }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (!result) return;
      this.userService.addUserRole(this.selectedUser!.userId, this.selectedNewRole).subscribe({
        next: _ => this.loadUserRoles(this.selectedUser!.userId),
        error: (err) => {
          const msg = typeof err?.error === 'string' ? err.error : (err?.error?.message || 'add role failed');
          this.dialog.open(AcknowledgeDialogComponent, { data: msg });
        }
      });
    });
  }

  triggerEditSelected(): void { if (this.selectedUser) { this.editUser(this.selectedUser); } }
  triggerDeleteSelected(): void { if (this.selectedUser) { this.openDeleteConfirmDialog(this.selectedUser); } }
}
